import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, playerHeaders, useGameState, fmtClock, pointsIfCorrect, formatApiError } from "@/lib/api";
import { playDing, playBuzz, playFanfare, playAlert, playCheckIn, vibrate, unlockAudio } from "@/lib/sounds";
import Avatar from "@/components/Avatar";
import AvatarMaker from "@/components/AvatarMaker";
import { BadgeArt, BADGE_META } from "@/components/BadgeArt";
import { toast } from "sonner";
import { Brain, Flame, Gift, Loader2, Radio, TrendingUp, Trophy, Users } from "lucide-react";
import { BadgeRow } from "@/components/Badges";

const LETTERS = ["1", "2", "3", "4"];

function WinOverlay({ me, taunt }) {
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(null);

  useEffect(() => {
    playFanfare();
    vibrate([250, 120, 250, 120, 400]);
  }, []);

  async function spin() {
    setSpinning(true);
    try {
      const { data } = await api.post("/prizes/spin", {}, { headers: playerHeaders() });
      setPrize(data.prize);
      toast.success(`Prize won: ${data.prize.name}!`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSpinning(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-y-auto bg-navy-900/90 px-4 py-6 backdrop-blur-md"
      data-testid="play-win-overlay"
    >
      <motion.div
        initial={{ scale: 0.4, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 14 }}
        className="flex w-full max-w-[300px] flex-col items-center"
      >
        <img src="/brand/braintap-mascot.png" alt="" className="float-slow mb-2 w-20 drop-shadow-[0_0_30px_rgba(255,43,253,0.55)]" aria-hidden="true" />
        <Avatar icon={me.avatar_icon} color={me.avatar_color} config={me.avatar_config} prize={prize || (me.prizes || []).slice(-1)[0]} size={80} />
        <h2 className="animate-glow-pulse mt-3 rounded-2xl px-5 py-1.5 font-display text-3xl font-extrabold uppercase tracking-tight text-amber text-glow-amber">You win!</h2>
        <p className="mt-2.5 rounded-2xl bg-navy-800 px-5 py-2 text-center font-display text-base font-bold text-cream">"{taunt || "Haha, I won!"}"</p>
        <div className="mt-2.5 w-full"><BadgeRow badges={me.badges} /></div>
        {me.guest ? (
          <p className="mt-3 max-w-[260px] text-center text-xs text-mut" data-testid="play-guest-prize-note">
            Create a full player profile next game to earn badges, prizes and spins.
          </p>
        ) : prize ? (
          <motion.p
            initial={{ scale: 0.6 }}
            animate={{ scale: 1 }}
            className="mt-3 rounded-2xl bg-amber/15 px-5 py-2.5 font-display text-sm font-extrabold uppercase text-amber-soft"
            data-testid="play-prize"
          >
            You won: {prize.name}
          </motion.p>
        ) : !me.email_verified ? (
          <p className="mt-3 rounded-2xl bg-grape/15 px-5 py-2.5 text-center font-display text-xs font-extrabold uppercase tracking-wide text-[#dcb6ff]" data-testid="play-prize-locked">
            Confirm your email to spin the prize wheel
          </p>
        ) : (
          <button
            data-testid="play-spin-button"
            onClick={spin}
            disabled={spinning}
            className="mt-3 flex items-center gap-2 rounded-full bg-amber px-7 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 disabled:opacity-60"
          >
            {spinning ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />}
            Spin the prize wheel
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}

function ProfileEditor({ me, onSave, onClose }) {
  const [name, setName] = useState(me.name || "You");
  const [config, setConfig] = useState(me.avatar_config || null);
  const [pub, setPub] = useState(me.profile_public !== false);
  const [busy, setBusy] = useState(false);
  const guest = !localStorage.getItem("tk_player_token");
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/90 p-5 backdrop-blur-md" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="max-h-[88vh] w-full max-w-sm overflow-y-auto rounded-3xl border-2 border-magenta/40 bg-navy-800 p-6"
        onClick={(e) => e.stopPropagation()} data-testid="play-profile-editor">
        <h2 className="mb-4 font-display text-xl font-extrabold uppercase text-amber">Your profile</h2>
        {guest ? (
          <p className="py-4 text-sm text-mut">Create a full player profile next game (with email) to pick your character and appear on the Champions Wall.</p>
        ) : (
          <>
            <div className="mb-4 flex justify-center">
              <Avatar icon={me.avatar_icon} color={me.avatar_color} config={config} size={88} />
            </div>
            <input data-testid="profile-name-input" value={name} maxLength={24}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-center font-display text-lg font-bold text-cream outline-none focus:border-amber" />
            <div className="my-4">
              <AvatarMaker config={config} onChange={(c) => setConfig(c)} />
            </div>
            <button type="button" data-testid="profile-public-toggle" onClick={() => setPub(!pub)}
              className={`flex w-full items-center justify-between rounded-2xl border px-5 py-3 text-left ${pub ? "border-mint/50 bg-mint/10" : "border-ice/15 bg-navy-900/60"}`}>
              <span>
                <span className="block text-sm font-bold text-cream">{pub ? "Public profile" : "Private profile"}</span>
                <span className="block text-[11px] text-mut">{pub ? "You appear on the Champions Wall" : "Hidden from the Champions Wall"}</span>
              </span>
              <span className={`flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors ${pub ? "bg-mint" : "bg-navy-700"}`}>
                <span className={`h-5 w-5 rounded-full bg-white transition-transform ${pub ? "translate-x-5" : ""}`} />
              </span>
            </button>
            <button data-testid="profile-save" disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                await onSave({ name: name.trim() || "You", avatar_config: config, profile_public: pub });
                onClose();
              } finally { setBusy(false); }
            }} className="neon-btn mt-4 w-full rounded-full py-3 font-display text-sm font-extrabold uppercase tracking-wide disabled:opacity-60">
              Save profile
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function AdCard({ ad, onGone }) {
  const [gone, setGone] = useState(false);
  if (!ad || gone) return null;
  const open = () => {
    api.post(`/ads/${ad.venue_code}/click`).catch(() => {});
    if (ad.link_url) window.open(ad.link_url, "_blank", "noopener");
    else onGone && onGone();
  };
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={open}
      className="w-full overflow-hidden rounded-2xl border-2 border-magenta/40 bg-navy-800 text-left"
      data-testid="play-ad-card"
    >
      {ad.media_type === "video" ? (
        <video src={ad.media_url} muted loop autoPlay playsInline className="h-36 w-full bg-black object-cover" />
      ) : (
        <img src={ad.media_url} alt={ad.venue_name} className="h-36 w-full bg-black object-cover" />
      )}
      <div className="p-3">
        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#ff9df2]">Sponsored</div>
        <div className="mt-0.5 font-display text-base font-extrabold text-cream">{ad.venue_name}</div>
        {ad.title && <div className="text-xs font-bold text-amber-soft">{ad.title}</div>}
        <div className="mt-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-mint">
          {ad.link_url ? "Tap to visit their site →" : "Tap for more"}
        </div>
      </div>
    </motion.button>
  );
}

function PlaceCard({ places }) {
  const [view, setView] = useState(0);
  if (!places) return null;
  const views = [];
  if (places.venue != null) views.push({ rank: places.venue, scope: `at ${places.venue_name || "the bar"} tonight` });
  if (places.region != null) views.push({ rank: places.region, scope: `in ${places.region_name || "your region"} this week` });
  if (places.nation != null) views.push({ rank: places.nation, scope: "nationally this week" });
  if (!views.length) return null;
  const v = views[view % views.length];
  const nextScope = views[(view + 1) % views.length].scope;
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.97 }}
      onClick={() => setView((x) => x + 1)}
      className="w-full rounded-2xl border-2 border-amber/50 bg-amber/10 px-5 py-4 text-center transition-colors hover:bg-amber/15"
      data-testid="play-my-place"
    >
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber-soft">Your place</div>
      <div className="mt-1 font-display text-2xl font-extrabold text-cream" data-testid="play-my-place-value">
        <span className="font-mono-num text-amber">#{v.rank}</span> <span className="text-base">{v.scope}</span>
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mut" data-testid="play-my-place-hint">
        Tap to see your place {nextScope}
      </div>
    </motion.button>
  );
}

function RewardPopup({ reward, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/90 p-6 backdrop-blur-md"
      data-testid="play-reward-popup"
    >
      <motion.div
        initial={{ scale: 0.5, y: 40 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        className="w-full max-w-sm rounded-3xl border-2 border-amber/50 bg-navy-800 p-8 text-center shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber/20">
          <Gift size={32} className="text-amber" />
        </div>
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mint">Reward unlocked</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold uppercase text-amber text-glow-amber" data-testid="play-reward-title">{reward.title}</h2>
        {reward.description && <p className="mt-2 text-cream">{reward.description}</p>}
        <p className="mt-4 text-sm text-mut">Earned at {reward.venue_name} — {reward.trigger === "wins" ? `${reward.games_required} first-place win${reward.games_required === 1 ? "" : "s"}` : `${reward.games_required} game${reward.games_required === 1 ? "" : "s"} played`}</p>
        <div className="mt-6 rounded-2xl border border-dashed border-amber/50 bg-amber/10 px-5 py-4 font-display font-extrabold uppercase tracking-wide text-amber-soft">
          Show this to the bartender
        </div>
        <button
          data-testid="play-reward-dismiss"
          onClick={onClose}
          className="mt-5 w-full rounded-full bg-amber py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5"
        >
          Awesome — got it
        </button>
      </motion.div>
    </motion.div>
  );
}

function CheckInCard({ code, checkedIn }) {
  const [val, setVal] = useState("");
  const [status, setStatus] = useState("idle"); // idle | busy | ok | wrong

  async function submit(e) {
    e.preventDefault();
    if (status === "busy" || status === "ok") return;
    if (val.length !== 3) return;
    setStatus("busy");
    try {
      const { data } = await api.post(`/game/${code}/checkin`, { code: val }, { headers: playerHeaders() });
      if (data.ok) {
        setStatus("ok");
        playCheckIn();
        vibrate([200, 100, 200]);
      } else {
        setStatus("wrong");
        vibrate(300);
      }
    } catch (err) {
      setStatus("wrong");
    }
  }

  if (checkedIn || status === "ok") {
    return (
      <div className="w-full rounded-2xl border-2 border-mint/50 bg-mint/10 px-5 py-4 text-center" data-testid="play-checkin-success">
        <div className="font-display text-lg font-extrabold uppercase text-mint">You're checked in</div>
        <div className="mt-1 text-sm text-mut">See you when the next game starts — don't wander far.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full rounded-2xl border-2 border-amber/50 bg-amber/10 px-5 py-4 text-center" data-testid="play-checkin-card">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Still here?</div>
      <div className="mt-1 text-sm text-cream">Type the <span className="font-bold text-amber-soft">3-digit code</span> on the big screen to stay in the game</div>
      <input
        data-testid="play-checkin-input"
        aria-label="3-digit check-in code shown on the big screen"
        value={val}
        onChange={(e) => { setVal(e.target.value.replace(/\D/g, "").slice(0, 3)); if (status === "wrong") setStatus("idle"); }}
        placeholder="000"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        className={`font-mono-num mx-auto mt-3 block w-36 rounded-2xl border-2 bg-navy-900/80 px-4 py-3 text-center text-4xl font-bold tracking-[0.3em] text-cream outline-none transition-colors ${status === "wrong" ? "border-coral" : "border-ice/25 focus:border-amber"}`}
      />
      <button
        type="submit"
        data-testid="play-checkin-submit"
        disabled={status === "busy" || val.length !== 3}
        className="mt-3 w-full rounded-full bg-amber py-3 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 disabled:opacity-50"
      >
        {status === "busy" ? <Loader2 size={16} className="mx-auto animate-spin" /> : "I'm here!"}
      </button>
      {status === "wrong" && (
        <p className="mt-2 text-sm font-bold text-coral" role="alert" data-testid="play-checkin-error">Wrong code — squint at the big screen and try again</p>
      )}
    </form>
  );
}

function VerifyNudge() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const iv = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(iv);
  }, [resendIn]);

  async function confirm() {
    if (code.length < 4 || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/player/verify", { code }, { headers: playerHeaders() });
      setDone(true);
      playCheckIn();
      vibrate(150);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (resendIn > 0) return;
    try {
      await api.post("/auth/player/verify/resend", {}, { headers: playerHeaders() });
      setResendIn(60);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e));
    }
  }

  if (done) {
    return (
      <div className="w-full rounded-2xl border-2 border-mint/50 bg-mint/10 px-5 py-3.5 text-center" data-testid="play-verify-success">
        <span className="font-display text-base font-extrabold uppercase text-mint">Email confirmed — prizes unlocked!</span>
      </div>
    );
  }

  return (
    <div className="w-full rounded-2xl border-2 border-grape/50 bg-grape/10 px-5 py-4 text-center" data-testid="play-verify-card">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#dcb6ff]">Confirm your email</div>
      <div className="mt-1 text-sm text-cream">Type the 6-digit code we emailed you to unlock prizes</div>
      <input
        data-testid="play-verify-input"
        aria-label="6-digit code from your email"
        value={code}
        onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setErr(""); }}
        onKeyDown={(e) => e.key === "Enter" && confirm()}
        placeholder="000000"
        inputMode="numeric"
        className={`font-mono-num mx-auto mt-3 block w-36 rounded-2xl border-2 bg-navy-900/80 px-4 py-2.5 text-center text-2xl font-bold tracking-[0.25em] text-cream outline-none transition-colors ${err ? "border-coral" : "border-ice/25 focus:border-grape"}`}
      />
      <button
        type="button"
        onClick={confirm}
        disabled={busy || code.length < 4}
        className="mt-3 w-full rounded-full bg-grape py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-cream transition-all hover:-translate-y-0.5 disabled:opacity-50"
        data-testid="play-verify-submit"
      >
        {busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Confirm my email"}
      </button>
      <button
        type="button"
        onClick={resend}
        disabled={resendIn > 0}
        className="mt-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-mut transition-colors hover:text-amber disabled:opacity-50"
        data-testid="play-verify-resend"
      >
        {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
      </button>
      {err && <p className="mt-2 text-xs font-bold text-coral" role="alert" data-testid="play-verify-error">{err}</p>}
    </div>
  );
}

function BreakPhotos({ venue }) {
  const photos = (venue.specials || []).filter((s) => s.image_url).map((s) => ({ url: s.image_url, cap: s.title || "" }))
    .concat((venue.gallery || []).map((g) => ({ url: g.image_url || g, cap: g.caption || "" })));
  const [i, setI] = useState(0);

  useEffect(() => {
    if (photos.length < 2) return undefined;
    const iv = setInterval(() => setI((x) => x + 1), 4500);
    return () => clearInterval(iv);
  }, [photos.length]);

  if (!photos.length) return null;
  const p = photos[i % photos.length];
  return (
    <div className="relative h-44 w-full overflow-hidden rounded-2xl border border-ice/15 bg-navy-800" data-testid="play-break-photos">
      <AnimatePresence>
        <motion.img
          key={i}
          src={p.url}
          alt={p.cap || "Venue special"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
      {p.cap && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy-900/95 to-transparent px-4 pb-2.5 pt-8 text-left font-display text-sm font-extrabold uppercase text-cream" data-testid="play-break-photo-caption">
          {p.cap}
        </div>
      )}
    </div>
  );
}

export default function Play() {
  const { code } = useParams();
  const { state, secondsLeft } = useGameState(code, true);
  const [picked, setPicked] = useState(null);
  const [reward, setReward] = useState(null);
  const [cityRank, setCityRank] = useState(null);
  const [myPlaces, setMyPlaces] = useState(null);
  const [phoneAds, setPhoneAds] = useState([]);
  const [adRot, setAdRot] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const badgeCountRef = useRef(null);
  useEffect(() => {
    if (!state || state.notFound || state.venue.type !== "home" || !code) return undefined;
    let alive = true;
    api.get(`/home-ads/${code}`).then(({ data }) => { if (alive) setPhoneAds(data.ads || []); }).catch(() => {});
    return () => { alive = false; };
  }, [state ? state.venue.type : null, code]);
  useEffect(() => {
    if (phoneAds.length < 2) return undefined;
    const iv = setInterval(() => setAdRot((i) => i + 1), 10000);
    return () => clearInterval(iv);
  }, [phoneAds.length]);
  // Badge earned toast — fires once when a new badge appears on your profile.
  useEffect(() => {
    const badges = state && state.me && state.me.badges;
    if (!badges) return;
    if (badgeCountRef.current !== null && badges.length > badgeCountRef.current) {
      const latest = badges[badges.length - 1];
      toast.custom((t) => (
        <div className="flex items-center gap-3 rounded-2xl border-2 border-amber/50 bg-navy-800 px-5 py-3.5 shadow-[0_0_40px_rgba(255,201,60,0.35)]" data-testid="play-badge-toast">
          <BadgeArt id={latest} size={38} />
          <div>
            <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-amber-soft">Badge earned</div>
            <div className="font-display text-sm font-extrabold text-cream">{(BADGE_META[latest] || {}).name || latest}</div>
          </div>
        </div>
      ), 4000);
    }
    badgeCountRef.current = badges.length;
  }, [state && state.me && state.me.badges]);
  const [announce, setAnnounce] = useState("");
  const lastAlertRef = useRef(null);
  const flashTimer = useRef(null);
  const [flash, setFlash] = useState(null);
  const [seenRewards, setSeenRewards] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tk_seen_rewards") || "[]"); } catch (e) { return []; }
  });
  const qKey = state && !state.notFound ? `${state.game_number}:${state.q_index}` : null;

  // Fresh question: clear the pick AND the browser's focus ring so the last
  // answer's highlight never bleeds into the next question.
  useEffect(() => {
    setPicked(null);
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  }, [qKey]);

  useEffect(() => { unlockAudio(); }, []);

  const rewards = (state && state.me && state.me.rewards) || [];
  const unseenKey = rewards.map((r) => `${r.venue_code}:${r.earned_at}`).find((k) => !seenRewards.includes(k));

  useEffect(() => {
    if (unseenKey && !reward) {
      setReward(rewards.find((r) => `${r.venue_code}:${r.earned_at}` === unseenKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenKey]);

  useEffect(() => {
    if (!state || state.notFound || !["break", "leaderboard"].includes(state.phase) || !code) return;
    let alive = true;
    api.get(`/game/${code}/boards`, { params: { player_id: (state.me && state.me.player_id) || "" } }).then(({ data }) => {
      if (!alive) return;
      const rows = data.city || [];
      const idx = rows.findIndex((r) => r.venue_code === state.venue.code);
      if (idx >= 0) setCityRank({ rank: idx + 1, total: rows.length, region: data.metro || state.venue.region });
      setMyPlaces(data.mine || null);
    }).catch(() => { /* board is optional on the phone */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state ? state.phase : null, code]);

  // Screen-reader narration — every phase change speaks the question & options.
  const livePhase = state && !state.notFound ? state.phase : null;
  const qIdx = state ? state.q_index : -1;
  const gameNum = state ? state.game_number : -1;
  useEffect(() => {
    if (!state || state.notFound) return;
    if (state.phase === "question" && state.question) {
      setAnnounce(`Question ${state.q_index + 1} of ${state.question_total}. ${state.question.text}. Options: ${state.question.options.map((o, i) => `${i + 1}. ${o}`).join(". ")}`);
    } else if (state.phase === "reveal" && state.question) {
      setAnnounce(`The correct answer is: ${state.question.options[state.question.correct]}. ${state.question.fact || ""}`);
    } else if (state.phase === "break") {
      setAnnounce(state.intermission_code ? "Intermission. Type the 3-digit check-in code shown on the big screen to stay in the game." : "Intermission. The next game starts soon.");
    } else if (state.phase === "leaderboard") {
      setAnnounce("Game over. Final standings are in.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePhase, qIdx, gameNum]);

  // Final 10 seconds of break: buzz + vibrate once a second so nobody misses kickoff.
  const secBucket = livePhase === "break" ? Math.ceil(secondsLeft) : -1;
  useEffect(() => {
    if (livePhase !== "break") { lastAlertRef.current = null; return; }
    if (secBucket <= 10 && secBucket > 0 && lastAlertRef.current !== secBucket) {
      lastAlertRef.current = secBucket;
      const urgent = secBucket <= 3;
      if (state.phone_sounds !== false) playAlert(urgent);
      vibrate(urgent ? [300, 120, 300] : 180);
      setAnnounce(`${secBucket} seconds until the next game starts`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePhase, secBucket]);

  function dismissReward() {
    if (reward) {
      const key = `${reward.venue_code}:${reward.earned_at}`;
      const next = [...seenRewards, key];
      setSeenRewards(next);
      try { localStorage.setItem("tk_seen_rewards", JSON.stringify(next)); } catch (e) { /* private mode */ }
    }
    setReward(null);
  }

  if (!state) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-900">
        <div className="font-mono text-sm uppercase tracking-[0.3em] text-mut">Taking your seat…</div>
      </div>
    );
  }

  if (state.notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-navy-900 px-6 text-center" data-testid="play-not-found">
        <h1 className="font-display text-3xl font-extrabold uppercase text-cream">Room not found</h1>
        <p className="text-mut">Check the code on the big screen and try again.</p>
        <Link to="/join" className="rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase text-navy-900">Back to join</Link>
      </div>
    );
  }

  if (state.maintenance) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-navy-900 px-6 text-center" data-testid="play-maintenance">
        <img src="/brand/braintap-mascot.png" alt="BrainTap mascot" className="float-slow w-28" />
        <h1 className="font-display text-3xl font-extrabold uppercase text-cream">Quick tune-up</h1>
        <p className="max-w-xs text-mut">BrainTap is in maintenance mode — the game pauses and comes right back.</p>
        {state.announcement?.message && <p className="rounded-2xl border border-amber/40 bg-amber/10 px-5 py-3 text-sm font-bold text-amber-soft">{state.announcement.message}</p>}
      </div>
    );
  }

  if (state.subscription_required) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-navy-900 px-6 text-center" data-testid="play-subscription-required">
        <img src="/brand/braintap-mascot.png" alt="BrainTap mascot" className="float-slow w-28" />
        <h1 className="font-display text-3xl font-extrabold uppercase leading-tight text-cream">The big screen is resting</h1>
        <p className="max-w-xs text-mut">
          {state.venue?.name || "This venue"} needs an active subscription before games run here. Your account, scores and badges are all safe.
        </p>
        {state.announcement?.message && <p className="rounded-2xl border border-grape/40 bg-grape/10 px-5 py-3 text-sm font-bold text-[#dcb6ff]">{state.announcement.message}</p>}
        <Link to="/venue" className="neon-btn rounded-full px-8 py-3.5 font-display font-extrabold uppercase tracking-wide">Venue sign-in</Link>
      </div>
    );
  }

  const me = state.me || { score: 0, rank: null, answered: false, avatar_icon: "Cat", avatar_color: "amber", name: "You" };
  const phase = state.phase;
  const frac = Math.max(0, Math.min(1, secondsLeft / (state.question_time || 10)));
  const isUrgent = secondsLeft <= 3;

  async function answer(choice) {
    if (picked !== null || me.answered) return;
    setPicked(choice);
    try {
      const { data } = await api.post(
        `/game/${code}/answer`,
        { q_index: state.q_index, choice },
        { headers: playerHeaders() }
      );
      toast[data.correct ? "success" : "error"](data.correct ? `Correct! +${data.points}` : "Wrong — 0 points");
      if (state.phone_sounds !== false) { if (data.correct) playDing(); else playBuzz(); }
      vibrate(data.correct ? 120 : 60);
      setFlash(data.correct ? "mint" : "coral");
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 460);
    } catch (e) {
      toast.error(formatApiError(e));
      setPicked(null);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-navy-900 text-cream"
      style={{ background: "radial-gradient(circle at 50% -10%, rgba(255,180,60,0.12), transparent 45%), radial-gradient(circle at 50% 110%, rgba(196,77,240,0.12), transparent 55%), #190a20" }}
      data-testid="play-page"
    >
      <header className="flex items-center justify-between border-b border-ice/10 px-4 py-3.5">
        <div>
          <div className="font-display text-sm font-extrabold uppercase tracking-wide">{state.venue.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
            Game <span className="text-amber">{String(state.game_number).padStart(4, "0")}</span> · Code {code}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {state.paused && (
            <span className="rounded-full bg-amber/20 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-soft" data-testid="play-paused-chip">Paused</span>
          )}
          <div className="text-right">
            <div className="font-mono-num text-xl font-bold leading-none text-mint">{me.score}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut">pts · {me.rank ? `#${me.rank}` : "—"}</div>
          </div>
          <button type="button" data-testid="play-avatar-button" aria-label="Edit your profile" onClick={() => setProfileOpen(true)}>
            <Avatar icon={me.avatar_icon} color={me.avatar_color} config={me.avatar_config} prize={(me.prizes || []).slice(-1)[0]} size={40} />
          </button>
          <img src="/brand/braintap-logo-full.png" alt="braintap.bar" className="h-14 w-auto" data-testid="play-braintap-logo" />
        </div>
      </header>

      {state.venue.logo_url && (
        <div className="flex justify-center border-b border-ice/10 pb-2.5 pt-3" data-testid="play-venue-logo">
          <img src={state.venue.logo_url} alt={state.venue.name} className="h-14 max-w-[70%] object-contain" />
        </div>
      )}

      <main className="flex flex-1 flex-col px-4 py-5">
        {phase === "question" && state.question && (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col" data-testid="play-question">
            <div className="mb-4 flex items-center justify-between">
              <span className="rounded-full border border-amber/40 bg-amber/10 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber-soft">
                Question {state.q_index + 1} / {state.question_total}
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
                <Radio size={12} className="text-coral" /> live
              </span>
            </div>
            <h1 className="mb-6 font-display text-2xl font-extrabold leading-snug" data-testid="play-question-text">{state.question.text}</h1>
            <div className="grid flex-1 grid-cols-1 gap-3">
              {state.question.options.map((opt, i) => {
                const isPicked = picked === i || (me.answered && me.my_choice === i);
                const answeredForMe = me.answered || picked !== null;
                return (
                  <button
                    key={i}
                    data-testid={`play-option-${i}`}
                    onClick={() => answer(i)}
                    disabled={answeredForMe}
                    className={`flex items-center gap-4 rounded-2xl border-2 p-4 text-left font-display text-base transition-all duration-200 ${
                      isPicked
                        ? "border-magenta bg-magenta/15 text-cream shadow-[0_0_18px_rgba(255,43,253,0.45)]"
                        : answeredForMe
                          ? "border-ice/10 bg-navy-800/50 text-mut opacity-50"
                          : "border-ice/20 bg-navy-800/70 text-cream hover:border-magenta hover:shadow-[0_0_16px_rgba(255,43,253,0.3)] active:scale-[0.97]"
                    }`}
                  >
                    <span className="font-mono-num flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-magenta to-coral text-sm font-bold shadow-[0_0_10px_rgba(255,43,253,0.4)]">{LETTERS[i]}</span>
                    {opt}
                  </button>
                );
              })}
            </div>

            {me.answered ? (
              <div className="mt-5 rounded-2xl border border-amber/40 bg-amber/10 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide text-amber-soft" data-testid="play-locked">
                Locked in · +{me.last_points} pts
                {me.streak >= 2 && (
                  <div className="mt-2 flex items-center justify-center gap-1.5 font-mono text-xs font-bold text-coral">
                    <Flame size={13} /> Streak ×{me.streak}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5">
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-navy-800">
                  <div className="h-full rounded-full" style={{ width: `${frac * 100}%`, background: "linear-gradient(90deg,#00e5ff,#ff2bfd,#ff2fa0)", boxShadow: "0 0 12px rgba(255,43,253,0.5)", transition: "width 0.25s linear" }} />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`font-mono-num text-2xl font-bold ${isUrgent ? "text-coral pulse-urgent" : "text-volt text-glow-volt"}`} data-testid="play-timer">{Math.ceil(secondsLeft)}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
                    Win up to <span className="text-mint">{pointsIfCorrect(secondsLeft, state.question_time)}</span> pts
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {phase === "reveal" && state.question && (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col" data-testid="play-reveal">
            <span className="mb-4 w-fit rounded-full border border-mint/40 bg-mint/10 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mint">
              Answer{state.question.category_name ? ` · ${state.question.category_name}` : ""}
            </span>
            <h1 className="mb-2 font-display text-2xl font-extrabold leading-snug">{state.question.text}</h1>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {state.question.options.map((opt, i) => {
                const isCorrect = state.question.correct === i;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-4 rounded-2xl border-2 p-4 font-bold ${
                      isCorrect
                        ? "border-mint bg-mint/15 text-mint"
                        : "border-ice/10 bg-navy-800/50 text-mut opacity-50"
                    }`}
                    data-testid={`play-reveal-option-${i}`}
                  >
                    <span className="font-mono-num flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy-900 text-sm">{LETTERS[i]}</span>
                    {opt}
                    {isCorrect && <span className="ml-auto font-mono text-[10px] uppercase tracking-widest">correct</span>}
                  </div>
                );
              })}
            </div>
            {state.question.fact && (
              <div className="mt-6 rounded-2xl border-l-4 border-amber bg-navy-800/80 p-5" data-testid="play-fact">
                <div className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Did you know</div>
                <p className="text-sm leading-relaxed text-ice">{state.question.fact}</p>
              </div>
            )}
            <p className="mt-auto pt-6 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-mut">Next question in {Math.ceil(secondsLeft)}s</p>
          </div>
        )}

        {phase === "leaderboard" && (
          <div className="mx-auto w-full max-w-md" data-testid="play-final">
            <h1 className="mb-6 text-center font-display text-2xl font-extrabold uppercase tracking-tight text-amber text-glow-amber">Final standings</h1>
            <div className="space-y-2.5">
              {state.standings.map((p, i) => (
                <div
                  key={p.player_id}
                  className={`flex items-center gap-3 rounded-2xl border p-3.5 ${i === 0 ? "medal-gold" : i === 1 ? "medal-silver" : i === 2 ? "medal-bronze" : p.player_id === me.player_id ? "border-magenta bg-magenta/10" : "border-ice/15 bg-navy-800/70"}`}
                  data-testid={`play-final-row-${i}`}
                >
                  <span className={`font-mono-num w-7 text-center font-bold ${i === 0 ? "medal-num-gold" : i === 1 ? "medal-num-silver" : i === 2 ? "medal-num-bronze" : "text-mut"}`}>{i + 1}</span>
                  <Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={34} />
                  <span className="flex flex-1 items-center gap-2 truncate font-bold">
                    {p.name}{p.player_id === me.player_id ? " (you)" : ""}
                    {p.streak >= 2 && (
                      <span className="flex items-center gap-1 rounded-full bg-coral/15 px-2 py-0.5 font-mono text-[10px] font-bold text-coral">
                        <Flame size={11} />×{p.streak}
                      </span>
                    )}
                  </span>
                  <span className="font-mono-num font-bold text-mint">{p.score}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <PlaceCard places={myPlaces} />
            </div>
            <AnimatePresence>
              {me.rank === 1 && <WinOverlay me={me} taunt={state.taunt} />}
            </AnimatePresence>
          </div>
        )}

        {phase === "break" && (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center text-center" data-testid="play-break">
            <img src="/brand/braintap-mascot.png" alt="BrainTap mascot" className="float-slow mb-4 w-24" data-testid="play-break-mascot" />
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-mut">Intermission</p>
            <div className="font-mono-num my-6 text-7xl font-bold text-amber text-glow-amber" data-testid="play-break-clock">{fmtClock(secondsLeft)}</div>
            <p className="mb-6 max-w-xs text-mut">Grab a round. The next game starts automatically.</p>
            {me.absent && (
              <div className="mb-5 w-full rounded-2xl border-2 border-coral/50 bg-coral/10 px-5 py-4 text-center" data-testid="play-benched-card">
                <div className="font-display text-lg font-extrabold uppercase text-coral">You're benched this game</div>
                <div className="mt-1 text-sm text-mut">You missed the check-in — you're back in the line-up for the next game.</div>
              </div>
            )}
            {!me.absent && state.intermission_code && (
              <div className="mb-5 w-full"><CheckInCard code={code} checkedIn={me.checked_in} /></div>
            )}
            {!me.guest && me.email_verified === false && (
              <div className="mb-5 w-full"><VerifyNudge /></div>
            )}
            {state.intermission_code && state.checked_in_count > 0 && (
              <p className="mb-5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-mut" data-testid="play-checkin-count">
                {state.checked_in_count} of {state.players_online} players checked in
              </p>
            )}
            <div className="mb-5 w-full"><BreakPhotos venue={state.venue} /></div>
            {cityRank && cityRank.total > 1 && cityRank.region && (
              <div className="mb-6 flex items-center gap-2 rounded-full border border-ice/20 bg-navy-800/70 px-5 py-2.5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#c9a3ff]" data-testid="play-city-chip">
                <Trophy size={12} className="shrink-0" />
                {state.venue.name} is #{cityRank.rank} of {cityRank.total} bars in {cityRank.region} this week
              </div>
            )}
            <div className="mb-6 w-full">
              <PlaceCard places={myPlaces} />
            </div>
            {state.advertising && state.advertising.enabled && state.advertising.phone_ads && (
              <div className="mb-6 w-full">
                {state.venue.type === "venue" && state.venue.home_ad && state.venue.home_ad.active ? (
                  <AdCard ad={{ ...state.venue.home_ad, venue_code: state.venue.code, venue_name: state.venue.name }} />
                ) : state.venue.type === "home" && phoneAds.length ? (
                  <AdCard ad={phoneAds[adRot % phoneAds.length]} />
                ) : null}
              </div>
            )}
            {state.winner && (
              <div className="mb-8 flex items-center gap-4 rounded-2xl border border-amber/40 bg-amber/10 px-6 py-4">
                <Avatar icon={state.winner.avatar_icon} color={state.winner.avatar_color} size={44} />
                <div className="text-left">
                  <div className="font-display font-extrabold uppercase text-amber-soft">{state.winner.name} won last game</div>
                  <div className="text-sm text-mut">"{state.taunt}"</div>
                </div>
              </div>
            )}
            {state.loyalty && (
              <div className="mb-6 w-full" data-testid="play-loyalty-card">
                {rewards.some((r) => r.venue_code === code) ? (
                  <div className="rounded-2xl border-2 border-mint/50 bg-mint/10 px-5 py-4 text-center">
                    <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mint">Your reward</div>
                    <div className="mt-1 font-display text-xl font-extrabold uppercase text-cream">{state.loyalty.title}</div>
                    <div className="mt-1 text-sm text-mint">Show this to the bartender</div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-ice/15 bg-navy-800/70 px-5 py-4 text-left">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-cream">{state.loyalty.title}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut" data-testid="play-loyalty-progress">
                        {Math.min(state.loyalty.progress, state.loyalty.games_required)}/{state.loyalty.games_required} {state.loyalty.trigger === "wins" ? "wins" : "games"}
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-navy-900">
                      <div className="h-full rounded-full bg-mint transition-all" style={{ width: `${Math.min(1, state.loyalty.progress / state.loyalty.games_required) * 100}%` }} />
                    </div>
                    <div className="mt-2 text-xs text-mut">{state.loyalty.trigger === "wins" ? "Take first place here — the reward pops up on your phone" : "Keep playing here — the reward pops up on your phone once you hit the mark"}</div>
                  </div>
                )}
              </div>
            )}
            {state.venue.specials && state.venue.specials.length > 0 && (
              <div className="w-full text-left">
                <div className="mb-3 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">
                  <TrendingUp size={12} /> Tonight's specials
                </div>
                <div className="space-y-2">
                  {state.venue.specials.slice(0, 3).map((s, i) => (
                    <div key={i} className="rounded-xl border border-ice/15 bg-navy-800/70 px-4 py-3">
                      <div className="text-sm font-bold text-cream">{s.title}</div>
                      <div className="text-xs text-mut">{s.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
              <Users size={12} /> {state.players_online} players in the room
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {flash && (
          <motion.div
            key={`${flash}-${qKey}`}
            initial={{ opacity: 0.55 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className={`pointer-events-none fixed inset-0 z-40 ${flash === "mint" ? "bg-[radial-gradient(circle_at_center,rgba(46,242,166,0.3),transparent_65%)]" : "bg-[radial-gradient(circle_at_center,rgba(255,47,160,0.3),transparent_65%)]"}`}
            aria-hidden="true"
            data-testid="play-answer-flash"
          />
        )}
      </AnimatePresence>

      <div className="sr-only" role="status" aria-live="assertive" aria-atomic="true" data-testid="play-announcer">{announce}</div>

      <footer className="border-t border-ice/10 px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
        braintap.bar · {state.venue.name}
      </footer>

      <AnimatePresence>
        {reward && <RewardPopup reward={reward} onClose={dismissReward} />}
        {profileOpen && (
          <ProfileEditor
            me={me}
            onClose={() => setProfileOpen(false)}
            onSave={async (patch) => {
              try {
                await api.patch("/player/me", patch, { headers: playerHeaders() });
                toast.success("Profile saved — see you on the Champions Wall");
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
